import {
  SearchEngine,
  SearchEngineConfiguration,
  buildContext,
  buildDictionaryFieldContext,
  buildSearchEngine,
  getOrganizationEndpoints,
} from '@coveo/atomic-react';
import { analyticsClientMiddleware } from '@lib/coveo/analyticsClientMiddleware';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { interceptSearchMiddleware } from '@lib/coveo/interceptSearchMiddleware';
import {
  getNewSearchToken,
  getSavedSearchToken,
  getSearchTokenGroups,
} from '@lib/coveo/searchTokenHelpers';

let searchEngine: SearchEngine | null = null;
let initPromise: Promise<SearchEngine> | null = null;

function getSearchEngineConfiguration(
  searchToken: string,
  analyticsEnabled: boolean,
  locale: string
): SearchEngineConfiguration {
  const organizationId = process.env.NEXT_PUBLIC_COVEO_ORGANIZATION_ID!;

  return {
    accessToken: searchToken,
    organizationId,
    organizationEndpoints: getOrganizationEndpoints(organizationId),
    renewAccessToken: () => getNewSearchToken(CoveoSearchHub.SEARCH),
    name: CoveoSearchHub.SEARCH,
    search: {
      searchHub: CoveoSearchHub.SEARCH,
      locale,
    },
    analytics: {
      enabled: analyticsEnabled,
      analyticsClientMiddleware,
    },
  };
}

export type InitEngineOptions = {
  locale: string;
  currency: string;
  analyticsEnabled: boolean;
};

function updateEngineContext(
  eng: SearchEngine,
  options: InitEngineOptions
): void {
  const { locale, currency } = options;
  const dictionaryContext = buildDictionaryFieldContext(eng);
  dictionaryContext.set({ ec_price: currency, ec_price_filter: currency });
  const context = buildContext(eng);
  context.add('language', locale);
}

/**
 * Initialize the Coveo Headless engine singleton.
 * Configures all 5 required pieces: accessToken, searchHub, userGroups, website, DictionaryFieldContext.
 * Must be called once before using `engine()`.
 */
export function initEngine(options: InitEngineOptions): Promise<SearchEngine> {
  if (searchEngine) {
    updateEngineContext(searchEngine, options);
    return Promise.resolve(searchEngine);
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = doInitEngine(options).catch((error) => {
    initPromise = null;
    throw error;
  });

  return initPromise;
}

async function doInitEngine(options: InitEngineOptions): Promise<SearchEngine> {
  const { locale, currency, analyticsEnabled } = options;

  const savedSearchToken = getSavedSearchToken(CoveoSearchHub.SEARCH);
  let searchToken: string | null = null;

  if (savedSearchToken && savedSearchToken !== '{}') {
    try {
      const parsed = JSON.parse(savedSearchToken);
      if (typeof parsed === 'string' && parsed.trim()) {
        searchToken = parsed;
      }
    } catch {
      // Ignore parse errors and fall back to fetching a new token below.
    }
  }

  if (!searchToken) {
    searchToken = await getNewSearchToken(CoveoSearchHub.SEARCH);
  }

  if (!searchToken) {
    throw new Error('Failed to initialize Coveo engine: missing search token.');
  }

  searchEngine = buildSearchEngine({
    configuration: getSearchEngineConfiguration(
      searchToken,
      analyticsEnabled,
      locale
    ),
    middlewares: [interceptSearchMiddleware],
  });

  // DictionaryFieldContext — currency for price fields
  const dictionaryContext = buildDictionaryFieldContext(searchEngine);
  dictionaryContext.set({ ec_price: currency, ec_price_filter: currency });

  // Context — website, language, userGroups
  const context = buildContext(searchEngine);
  context.add('website', 'assetstore');
  context.add('language', locale);
  context.add('userGroups', getSearchTokenGroups(searchToken) || []);

  return searchEngine;
}

/**
 * Sync accessor for the engine singleton.
 * Throws if `initEngine()` has not been called.
 */
export function engine(): SearchEngine {
  if (!searchEngine) {
    throw new Error(
      'Coveo engine not initialized. Call initEngine() before using engine().'
    );
  }
  return searchEngine;
}

/**
 * Reset the engine singleton (useful for testing).
 */
export function resetEngine(): void {
  searchEngine = null;
  initPromise = null;
}
